<script setup>
import { ref, watch, nextTick, onMounted } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useOrderStore } from '@/stores/OrderStore';
import { useProductStore } from '@/stores/productsStore';
import { useToast } from 'vue-toastification';
import { useErrorLogger } from '@/composables/useErrorLogger';
import axios from 'axios';

const toast = useToast();
const authStore = useAuthStore();
const orderStore = useOrderStore();
const productStore = useProductStore();
const { logError } = useErrorLogger();
const orderId = ref('');
const isLoading = ref(false);
const error = ref(null);
const orderIdInput = ref(null);
const loadManualOrder = async () => {
  if (!orderId.value.trim()) {
    toast.error('Please enter an order ID');
    return;
  }

  isLoading.value = true;
  error.value = null;
  
  try {
    const rawInput = orderId.value.trim();
    const trimmedOrderId = rawInput.startsWith('SAL-ORD-') ? rawInput.slice(8) : rawInput;
      
    const fullOrderNumber = `SAL-ORD-${trimmedOrderId}`;
    console.log('Loading order', fullOrderNumber);

    const response = await axios.get(
      `/api/Order/GetOrder?orderId=${encodeURIComponent(fullOrderNumber)}`,
      {
        headers: {
          'Authorization': `Bearer ${authStore.managerToken ? authStore.managerToken : authStore.token}`,
          'store-id': authStore.branchId
        }
      }
    );

    if (response.data.Status === 200 && response.data.Product.OrderType === 3) {
      toast.success('Order loaded successfully');
      console.log('Order data:', response.data);
      
      const invoice = response.data.Product.Invoice;

      orderStore.clearOrderItems();
      orderStore.isManualOrderLoading = true;
      orderStore.state.isManualOrder = true;

      const invoicePayments = Array.isArray(invoice.Payment) ? invoice.Payment : [];
      const hasPayment = invoicePayments.some(payment => payment?.ModeOfPayment);
      orderStore.setIsThisPaidManualOrder(hasPayment);
      orderStore.setManualOrderPayments(hasPayment ? invoicePayments : []);

      orderStore.setSaleOrderNumber(fullOrderNumber);
      orderStore.setSalesPersonId(invoice.SalesPersonId);
      orderStore.setCustomer(invoice.Customer);
      orderStore.setTaxesFManualOrder(invoice.Taxes);
      orderStore.setOverallDiscount(invoice.OverallDiscount);

      const orderItems = invoice.OrderItems;


      for (const item of orderItems) {

        // Transform item
        const transformedItem = {
          Sku: item.Sku,
          RetailSku: item.RetailSku,
          Name: item.Name,
          Price: item.Price,
          ImageUrl: item.ImageUrl,
          Qty: item.Qty,
          // MaxQty: item.Qty, 
          Discount: item.Discount || 0
        };
        
        orderStore.addOrderItem(transformedItem);
        if (item.Discount > 0) {
          orderStore.updateDiscountPercentage(
            { ItemId: item.Sku }, 
            item.Discount
          );
        }
      }
      
      // Load printable comments into orderNotes
      if (Array.isArray(invoice.Comments)) {
        invoice.Comments.forEach(c => {
          if (c.Text && c.Text.startsWith('printable:')) {
            orderStore.addOrderNote(c.Text);
          }
        });
      }

      authStore.toggleManualOrderPopup();
      orderStore.isManualOrderLoading = false;
    } else {
      orderStore.setIsThisPaidManualOrder(false);
      orderStore.isManualOrderLoading = false;
      toast.error('Manual Order Not Found!');
    }
  } catch (err) {
    orderStore.setIsThisPaidManualOrder(false);
    orderStore.isManualOrderLoading = false;
    error.value = err.response?.data?.Message || err.Message;
    toast.error(`Failed to load order: ${error.value}`);
    console.error('API Error:', err);
    logError({ callStack: err?.stack || String(err), errorDetails: { message: err?.message, httpStatus: err?.response?.status, component: 'LoadManualOrderPopup', action: 'loadManualOrder', orderId: orderId.value, branchId: authStore.branchId } });
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  if (authStore.isManualOrderPopupVisible) {
    nextTick(() => {
      if (orderIdInput.value) {
        orderIdInput.value.focus();
      }
    });
  }
});
</script>

<template>
  <Teleport to="body">
    <Transition name="modal-outer">
      <div v-show="authStore.isManualOrderPopupVisible" 
           @click.self="authStore.toggleManualOrderPopup"
           class="absolute w-full bg-black bg-opacity-30 h-screen top-0 left-0 flex justify-center px-8">
        <Transition name="modal-inner" class="rounded-2xl">
          <div v-if="authStore.isManualOrderPopupVisible"
               class="fixed top-10 z-50 flex items-center justify-center bg-black bg-opacity-50 w-10/12">
            <div class="bg-white rounded-2xl shadow-lg p-6 w-full">
              <button @click="authStore.toggleManualOrderPopup"
                class="absolute right-2 top-2 text-gray-500 hover:text-gray-700">
                <i class="pi pi-times pr-2"></i>
              </button>
              <h2 class="font-semibold text-[24px] pb-4">Load Manual Order</h2>
              <div class="flex items-center">
                <span class="bg-gray-100 px-3 p-2 rounded-l-md border-0">SAL-ORD-</span>
                <input
                  ref="orderIdInput"
                  type="text"
                  v-model="orderId"
                  @keyup.enter="loadManualOrder"
                  placeholder="Enter order id"
                  class="flex-1 px-3 py-2 bg-gray-100 border-none rounded-r-md focus:outline-none"
                  :disabled="isLoading"
                />
                <button
                  @click="loadManualOrder"
                  :disabled="isLoading"
                  class="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-md transition-colors ml-4 disabled:opacity-50"
                >
                  <span v-if="!isLoading">Load Order</span>
                  <span v-else>Loading...</span>
                </button>
              </div>
              <div v-if="error" class="text-red-500 mt-2">{{ error }}</div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>


<style scoped>
.modal-outer-enter-active,
  .modal-outer-leave-active {
    transition: opacity 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
  }

  .modal-outer-enter-from,
  .modal-outer-leave-to {
    opacity: 0;
  }

  .modal-inner-enter-active {
    transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12) 0.15s;
  }
  
  .modal-inner-leave-active {
    transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
  }

  .modal-inner-enter-from {
    opacity: 0;
    transform: scale(0.8);
  }

  .modal-inner-leave-to {
    transform: scale(0.8);
  }
</style>